import * as imgUtil from "@shared/imgUtil.js";
import { Rect } from "@shared/Rect.js";
import { ArrayUtil } from "@shared/utility.js";

const debugBgColor1 = "#59c4f6";
const debugBgColor2 = "#f9cc9e";

export class ContentRendererUtils {
    
    static placeImage(ctx, imgOrCanvas, drawRect, stickerize, cyoDesign = null) {
        // Safety check: don't try to draw zero-dimension images
        if (!imgOrCanvas || imgOrCanvas.width === 0 || imgOrCanvas.height === 0) {
            console.warn("Skipping placeImage: image has zero dimensions");
            return;
        }

        const shadowOffset = imgUtil.outlineSizeFor(drawRect.width, drawRect.height);

        // Draw debug background if enabled and cyoDesign is provided
        if (cyoDesign && cyoDesign.debugOptions.drawBgs) {
            ctx.fillStyle = "rgba(89, 196, 246, 0.3)"; // Translucent blue
            ctx.fillRect(drawRect.x, drawRect.y, drawRect.width, drawRect.height);
        }

        if (stickerize) {
            ContentRendererUtils._stickerizeContext(ctx, shadowOffset);
        }

        ctx.drawImage(imgOrCanvas, drawRect.x, drawRect.y, drawRect.width, drawRect.height);

        if (stickerize) {
            // draw more shadows in other directions
            for (let i = 0; i<7; i++) {
                if (i==0) {
                    ctx.shadowOffsetX = shadowOffset;
                    ctx.shadowOffsetY = shadowOffset;
                } else if (i==1) {
                    ctx.shadowOffsetX = 0;
                    ctx.shadowOffsetY = shadowOffset;			    	
                } else if (i==2) {
                    ctx.shadowOffsetX = -shadowOffset;
                    ctx.shadowOffsetY = shadowOffset;			   
                } else if (i==3) {
                    ctx.shadowOffsetX = -shadowOffset;
                    ctx.shadowOffsetY = 0;		    	
                } else if (i==4) {
                    ctx.shadowOffsetX = -shadowOffset;
                    ctx.shadowOffsetY = -shadowOffset;			    	
                } else if (i==5) {
                    ctx.shadowOffsetX = 0;
                    ctx.shadowOffsetY = -shadowOffset;		    	
                } else if (i==6) {
                    ctx.shadowOffsetX = shadowOffset;
                    ctx.shadowOffsetY = -shadowOffset;			    	
                }
                ctx.drawImage(imgOrCanvas, drawRect.x, drawRect.y, drawRect.width, drawRect.height);
            }

            ContentRendererUtils._destickerizeContext(ctx);
        }
    }

    // Returns a random [x,y] origin location with which to place the next sticker (for pattern random alg).
    static getNextLoc(inDrawRect, canvasRect, placedDrawRects, spacing, randomState = null) {
        // NB: inDrawRect only has w/h set (not x/y)
        
        const maxFailedAttempts = 1000;
        
        // safeRect: the cropped area that is safe to draw into.
        // Since we're returning a _center point_, this should be inset from existing canvasRect by half the inDrawRect w/h.
        const safeRect = canvasRect.rectInsetBy([
            inDrawRect.height/2, 
            inDrawRect.width/2, 
            inDrawRect.height/2, 
            inDrawRect.width/2
        ]);
        
        var failCnt = 0;
        while (failCnt < maxFailedAttempts) {
            const x = safeRect.x + (randomState ? randomState.random() : Math.random()) * safeRect.width;
            const y = safeRect.y + (randomState ? randomState.random() : Math.random()) * safeRect.height;
            
            // make candidate drawing rect, adjusting x/y to center
            const candidateRect = new Rect(
                x - inDrawRect.width/2, 
                y - inDrawRect.height/2, 
                inDrawRect.width,
                inDrawRect.height);
                
            var overlaps = false;
            
            for (const placedDrawRect of placedDrawRects) {
                const placedDrawRectWithSpacing = placedDrawRect.rectInsetBy([-spacing, -spacing, -spacing, -spacing]);
                if (candidateRect.intersectsWithRect(placedDrawRectWithSpacing)) {
                    overlaps = true;
                    break;
                }
            }
            
            if (overlaps) {
                failCnt += 1;
                continue;
            }
            
            // success - we have a good candidate.
            return [x, y];
        }
        
        // tried too many times without success; return null
        return null;
    }

    static getRandomOrderArray(len, randomState = null) {
        const arr = Array.from({ length: len }, (_, i) => i);

        for (let i = arr.length - 1; i > 0; i--) {
            const j = randomState ? randomState.randomInt(0, i + 1) : Math.floor(Math.random() * (i + 1));
            [arr[i], arr[j]] = [arr[j], arr[i]];
        }

        return arr;
    }

    static _stickerizeContext(ctx, offset) {
        const x = 255;
        ctx.shadowColor = `rgba(${x}, ${x}, ${x}, 1.0)`;
        ctx.shadowBlur = 0;
        ctx.shadowOffsetX = offset;
        ctx.shadowOffsetY = 0;
    }

    static _destickerizeContext(ctx) {
        ctx.shadowBlur = 0;
        ctx.shadowOffsetX = 0;
        ctx.shadowOffsetY = 0;
    }

    static debugAddBg(canvas, color) {
        const ctx = canvas.getContext("2d");
        ctx.globalCompositeOperation = "destination-over";
        ctx.fillStyle = color;
        ctx.fillRect(0, 0, canvas.width, canvas.height);
        ctx.globalCompositeOperation = "source-over";    
    }

    static get debugBgColor1() { return debugBgColor1; }
    static get debugBgColor2() { return debugBgColor2; }
}